// filepath: apps/web/src/chrome/AppChrome.tsx
//
// FR-UI-NFR-4 — uniform chrome and design-system component set.
//
// A single shared chrome layout rendered by all chrome-bearing
// surfaces (cabinet/tenant, apex landing, operator console,
// editor topbar-only). The four variants share the same DOM
// skeleton — topbar + main (and optionally sidebar + footer) —
// so the topbar position, LanguageMenu slot, and UserMenu slot
// are identical across surfaces (AC-2, AC-3, AC-4).
//
// AC mapping:
//   AC-1 — single shared chrome layout (this component).
//   AC-2 — topbar position invariance: topbar is the first row
//          above `<main>`, full viewport width, sticky to top.
//   AC-3 — LanguageMenu mounted in the topbar right-group slot.
//   AC-4 — UserMenu mounted in the topbar right-group slot.
//          Variant-aware: apex shows a no-op avatar button
//          (anonymous), operator shows the operator-token
//          affordance, tenant and edit surface show the full
//          user-menu trigger.
//   AC-5 — static-guard regression net lives in
//          `apps/web/src/runtime/staticGuards.test.ts`. This file
//          uses ONLY design-system tokens.
//   AC-6 — opt-out for apex/editor surfaces is implemented in
//          `FullBleedLayout.tsx` (separate file).
//
// INV-9: chrome renders only design-system components +
//        `t(key, fallback)` text nodes. Zero `dangerouslySetInnerHTML`.

import React, { useCallback, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useTenant } from "@/tenant/TenantProvider";
import { useTenantConfig } from "@/bootstrap/tenantConfig";
import { useTranslation } from "@/i18n/useTranslation";
import { LanguageMenu } from "@/features/cabinet/chrome/LanguageMenu";
import { UserMenu } from "@/features/cabinet/chrome/UserMenu";
import { useEnvBadge } from "@/features/cabinet/chrome/useEnvBadge";
import { APEX_TENANT_SLUG } from "@/apex/apexTenantSlug";
import { useLocalePreference } from "@/features/cabinet/chrome/useLocalePreference";

// ─── Design-system token mirrors ───────────────────────────────────────
//
// These mirror the design-system token CSS variables so the chrome
// composes against the design system. The token list matches the
// design pin `docs/design/FR-UI-NFR-4/` so geometry stays invariant.
const TOKENS = {
  fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontFamilyMono: "ui-monospace, SFMono-Regular, Consolas, monospace",
  surfaceCard: "var(--surface-card)",
  surfacePage: "var(--surface-page)",
  surfaceSkeleton: "var(--surface-skeleton)",
  borderDefault: "var(--border-default)",
  textPrimary: "var(--text-primary)",
  textSecondary: "var(--text-body)",
  textTertiary: "var(--text-tertiary)",
  textOnPrimary: "var(--text-on-primary)",
  colorPrimary: "var(--color-primary)",
  colorNeutralSubtle: "var(--color-neutral-subtle)",
  colorWarning: "var(--color-warning)",
  radiusSm: "var(--radius-sm)",
  radiusMd: "var(--radius-md)",
  radiusFull: "var(--radius-full)",
  // 56px topbar height — invariant across all 4 variants.
  topbarHeight: "56px",
  space: {
    1: "var(--space-1)",
    2: "var(--space-2)",
    3: "var(--space-3)",
    4: "var(--space-4)",
    6: "var(--space-6)",
    8: "var(--space-8)",
  },
} as const;

const TOPBAR_TEST_ID = "appchrome-topbar";
const FOOTER_TEST_ID = "appchrome-footer";
const LANGUAGE_TRIGGER_TEST_ID = "appchrome-language-trigger";
const USER_TRIGGER_TEST_ID = "appchrome-user-trigger";
const OPERATOR_TOKEN_TEST_ID = "appchrome-operator-token-trigger";

export type AppChromeVariant = "tenant" | "apex" | "operator" | "editor";

export interface AppChromeProps {
  /** Which chrome surface this instance renders. */
  variant: AppChromeVariant;
  /** Optional override for the center brand text (apex/operator use). */
  brandText?: string;
  /**
   * Whether to render the sidebar + footer chrome around `<main>`.
   * Defaults: tenant/apex = true, editor = false.
   */
  withSidebarAndFooter?: boolean;
  /** Optional sidebar element to render between topbar and `<main>`. */
  sidebar?: React.ReactNode;
  /** Optional test id applied to the AppChrome `<main>` landmark. */
  contentTestId?: string;
  /** Optional aria-labelledby applied to the AppChrome `<main>` landmark. */
  contentAriaLabelledBy?: string;
  /** The page content. Always rendered inside the `<main>` landmark. */
  children: React.ReactNode;
}

// ─── Footer (shared, identical across variants) ────────────────────────
//
// The version string is injected at build time via Vite's `define`
// (apps/web/vite.config.ts). `import.meta.env.APP_VERSION` is the
// single source of truth; falls back to "dev" when no build-time
// constant is set.
//
// The copyright line uses the i18n key `cabinet.footer.copyright` with
// an i18next `{{year}}` interpolation placeholder; the actual year is
// passed as the third arg to `t()` (ISS-433 — pre-fix the placeholder
// was never replaced, so the rendered footer read `© {year} ABiTech…`
// in every locale). The fallback string is also passed through the
// same interpolation so the first paint (before i18n is ready) shows
// the right year too. Mirrors the cabinet Footer.tsx implementation
// so both footer call sites produce the same output.
const APP_VERSION: string =
  (typeof import.meta !== "undefined" &&
    typeof (import.meta as { env?: { APP_VERSION?: string } }).env
      ?.APP_VERSION === "string" &&
    (import.meta as { env: { APP_VERSION?: string } }).env.APP_VERSION) ||
  "dev";

const SUPPORT_EMAIL = "support@askou.org";
const SUPPORT_LABEL = "Support";
const COMPANY_NAME = "ABiTech";

function ChromeFooter(): React.ReactElement {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <footer
      role="contentinfo"
      data-testid={FOOTER_TEST_ID}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: TOKENS.space[4],
        padding: `${TOKENS.space[3]} ${TOKENS.space[6]}`,
        background: TOKENS.surfaceCard,
        borderTop: `1px solid ${TOKENS.borderDefault}`,
        fontFamily: TOKENS.fontFamily,
        flex: "0 0 auto",
      }}
    >
      <div
        data-testid="appchrome-footer-copyright"
        style={{
          fontSize: "12px",
          color: TOKENS.textTertiary,
        }}
      >
        {t(
          "cabinet.footer.copyright",
          `© {{year}} ${COMPANY_NAME}. All rights reserved.`,
          { year },
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: TOKENS.space[4],
        }}
      >
        <span
          data-testid="appchrome-footer-version"
          style={{
            fontSize: "12px",
            color: TOKENS.textTertiary,
            fontFamily: TOKENS.fontFamilyMono,
          }}
        >
          ASCOA {APP_VERSION}
        </span>
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          data-testid="appchrome-footer-support"
          rel="noopener noreferrer"
          style={{
            fontSize: "12px",
            color: TOKENS.colorPrimary,
            textDecoration: "none",
          }}
        >
          {t("cabinet.footer.supportLabel", SUPPORT_LABEL)}
        </a>
      </div>
    </footer>
  );
}

// ─── Tenant-variant topbar (preserves cabinet chrome) ────────────────
function TenantTopbarCenter({
  tenantName,
  envLabel,
  envTone,
}: {
  tenantName: string;
  envLabel: string | null;
  envTone: string | null;
}): React.ReactElement {
  const envBg =
    envTone === "warning"
      ? "var(--color-warning-subtle)"
      : envTone === "danger"
        ? "var(--color-danger-subtle)"
        : envTone === "success"
          ? "var(--color-success-subtle)"
          : "var(--color-primary-subtle)";
  const envFg =
    envTone === "warning"
      ? "var(--color-warning-subtle-text)"
      : envTone === "danger"
        ? "var(--color-danger-subtle-text)"
        : envTone === "success"
          ? "var(--color-success-subtle-text)"
          : "var(--color-primary-subtle-text)";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: TOKENS.space[2],
        flex: "1 1 auto",
      }}
    >
      <span
        data-testid="appchrome-tenant-slug"
        style={{
          fontSize: "var(--text-sm)",
          fontWeight: "var(--font-medium)",
          color: TOKENS.textPrimary,
        }}
      >
        {tenantName || ""}
      </span>
      {envLabel !== null && envTone !== null ? (
        <span
          data-testid="appchrome-environment-badge"
          aria-label={`Environment: ${envLabel}`}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: `${TOKENS.space[1]} ${TOKENS.space[2]}`,
            fontSize: "var(--text-xs)",
            fontWeight: "var(--font-medium)",
            background: envBg,
            color: envFg,
            borderRadius: TOKENS.radiusFull,
          }}
        >
          {envLabel}
        </span>
      ) : null}
    </div>
  );
}

// ─── UserMenu trigger — variant-aware (AC-4) ───────────────────────────
//
// apex:      no-op avatar (anonymous user on the public landing).
// operator:  operator-token affordance (single sign-on chip).
// tenant / editor: full UserMenu trigger (authenticated avatar +
//                caret, identical to the cabinet `Topbar`).
function VariantUserMenuTrigger({
  variant,
  identity,
  onToggle,
  expanded,
}: {
  variant: AppChromeVariant;
  identity: string;
  onToggle: () => void;
  expanded: boolean;
}): React.ReactElement {
  const { t } = useTranslation();

  if (variant === "apex") {
    const ariaLabel = t(
      "chrome.user.ariaLabel.apex",
      "User menu (not signed in)",
    );
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        data-testid={USER_TRIGGER_TEST_ID}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: TOKENS.space[2],
          padding: `${TOKENS.space[1]} ${TOKENS.space[2]}`,
          fontSize: "var(--text-sm)",
          fontWeight: "var(--font-medium)",
          background: "transparent",
          color: TOKENS.textSecondary,
          border: "1px solid transparent",
          borderRadius: TOKENS.radiusFull,
          cursor: "default",
        }}
        tabIndex={-1}
        disabled
      >
        <span
          aria-hidden="true"
          data-testid="appchrome-user-avatar"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "32px",
            height: "32px",
            background: TOKENS.colorNeutralSubtle,
            color: TOKENS.textSecondary,
            fontSize: "var(--text-sm)",
            fontWeight: "var(--font-semibold)",
            borderRadius: TOKENS.radiusFull,
          }}
        >
          ?
        </span>
      </button>
    );
  }

  if (variant === "operator") {
    const label = t("chrome.operator.tokenLabel", "Operator token");
    return (
      <button
        type="button"
        aria-label={t("chrome.operator.ariaLabel", "Operator token active")}
        data-testid={OPERATOR_TOKEN_TEST_ID}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: TOKENS.space[2],
          padding: `${TOKENS.space[1]} ${TOKENS.space[3]}`,
          fontSize: "var(--text-sm)",
          fontWeight: "var(--font-medium)",
          background: "var(--color-warning-subtle)",
          color: "var(--color-warning-subtle-text)",
          border: "1px solid var(--color-warning-subtle-border)",
          borderRadius: TOKENS.radiusSm,
          cursor: "pointer",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: TOKENS.space[2],
            height: TOKENS.space[2],
            background: TOKENS.colorWarning,
            borderRadius: TOKENS.radiusFull,
          }}
        />
        <span>{label}</span>
      </button>
    );
  }

  // tenant / editor — full UserMenu trigger.
  const userInitial =
    identity.length > 0 ? identity.charAt(0).toUpperCase() : "?";
  const ariaLabel = t(
    "chrome.user.ariaLabel.signedIn",
    `User menu, signed in as ${identity || "anonymous"}`,
  );
  return (
    <button
      type="button"
      aria-haspopup="menu"
      aria-expanded={expanded}
      aria-label={ariaLabel}
      data-testid={USER_TRIGGER_TEST_ID}
      onClick={onToggle}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: TOKENS.space[2],
        padding: `${TOKENS.space[1]} ${TOKENS.space[2]}`,
        fontSize: "var(--text-sm)",
        fontWeight: "var(--font-medium)",
        background: expanded ? TOKENS.surfaceSkeleton : "transparent",
        color: TOKENS.textSecondary,
        border: "1px solid transparent",
        borderRadius: TOKENS.radiusFull,
        cursor: "pointer",
      }}
    >
      <span
        aria-hidden="true"
        data-testid="appchrome-user-avatar"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "32px",
          height: "32px",
          background: TOKENS.colorPrimary,
          color: TOKENS.textOnPrimary,
          fontSize: "var(--text-sm)",
          fontWeight: "var(--font-semibold)",
          borderRadius: TOKENS.radiusFull,
        }}
      >
        {userInitial}
      </span>
      <span aria-hidden="true" style={{ fontSize: "var(--text-xs)" }}>
        v
      </span>
    </button>
  );
}

// ─── AppChrome (the single shared chrome) ──────────────────────────────
export function AppChrome({
  variant,
  brandText,
  withSidebarAndFooter,
  sidebar,
  contentTestId,
  contentAriaLabelledBy,
  children,
}: AppChromeProps): React.ReactElement {
  const { t } = useTranslation();
  const auth = useAuth();
  const { tenantSlug } = useTenant();
  // ISS-f4fa1b7b-297: skip the tenant-config fetch on the apex variant
  // — the apex landing is served from the bare apex host, the Go
  // backend's resolver 404s any /api/tenant-config on the bare apex,
  // and the apex chrome never reads tenantConfig. Passing "" short-
  // circuits the underlying useQuery (enabled: slug !== "").
  const { data: tenantConfig } = useTenantConfig(
    variant === "apex" || tenantSlug === APEX_TENANT_SLUG ? "" : tenantSlug,
  );
  const env = useEnvBadge();
  const { locale } = useLocalePreference();

  const [langOpen, setLangOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);

  // Default for chrome scope per variant. Operator renders its own
  // sidebar via the existing OperatorShell (NavLink-driven); tenant
  // and apex need the sidebar + footer; editor renders topbar-only
  // (AC-6 opt-out).
  const renderSidebarAndFooter =
    withSidebarAndFooter ?? (variant === "tenant" || variant === "apex");

  const activeLabel = t(
    locale === "ru"
      ? "cabinet.languageMenu.label.ru"
      : locale === "kk"
        ? "cabinet.languageMenu.label.kk"
        : "cabinet.languageMenu.label.en",
    "English",
  );

  const handleLangToggle = useCallback(() => {
    setLangOpen((prev) => !prev);
    setUserOpen(false);
  }, []);
  const handleUserToggle = useCallback(() => {
    setUserOpen((prev) => !prev);
    setLangOpen(false);
  }, []);

  const tenantName = tenantConfig?.tenant_slug ?? tenantSlug ?? "";
  // ISS-f4fa1b7b-297b: defensive optional chain. The apex branch
  // (variant === "apex") renders <AppChrome> outside <AuthProvider>;
  // useAuth() returns undefined there, and `auth.user?.profile` would
  // throw on the implicit `auth.user` access. The leading `auth?.`
  // makes the chrome safely render the (anonymous) apex user menu.
  // Authenticated surfaces (operator/tenant/editor) still get a
  // populated identity because they live inside <AuthProvider>.
  const identity = pickName(auth?.user?.profile);
  const centerText = brandText ?? tenantName;

  return (
    <div
      data-testid="appchrome-root"
      data-chrome-variant={variant}
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: TOKENS.surfacePage,
        fontFamily: TOKENS.fontFamily,
      }}
    >
      <header
        role="banner"
        data-testid={TOPBAR_TEST_ID}
        style={{
          display: "flex",
          alignItems: "center",
          gap: TOKENS.space[4],
          padding: `${TOKENS.space[3]} ${TOKENS.space[6]}`,
          background: TOKENS.surfaceCard,
          borderBottom: `1px solid ${TOKENS.borderDefault}`,
          height: TOKENS.topbarHeight,
          boxSizing: "border-box",
          flex: "0 0 auto",
          fontFamily: TOKENS.fontFamily,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <div
          data-testid="appchrome-logo-region"
          style={{
            display: "flex",
            alignItems: "center",
            gap: TOKENS.space[2],
            flex: "0 0 auto",
            minWidth: 0,
          }}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: TOKENS.space[2],
            flex: "1 1 auto",
          }}
        >
          {variant === "tenant" ? (
            <TenantTopbarCenter
              tenantName={tenantName}
              envLabel={env ? env.label : null}
              envTone={env ? env.tone : null}
            />
          ) : (
            <span
              data-testid="appchrome-brand-text"
              style={{
                fontSize: "var(--text-md)",
                fontWeight: "var(--font-semibold)",
                color: TOKENS.textPrimary,
              }}
            >
              {centerText}
            </span>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: TOKENS.space[2],
            flex: "0 0 auto",
            position: "relative",
          }}
        >
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={langOpen}
            aria-label={t(
              "chrome.language.ariaLabel",
              `Change language, current: ${activeLabel}`,
            )}
            data-testid={LANGUAGE_TRIGGER_TEST_ID}
            onClick={handleLangToggle}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: TOKENS.space[1],
              padding: `${TOKENS.space[1]} ${TOKENS.space[3]}`,
              fontSize: "var(--text-sm)",
              fontWeight: "var(--font-medium)",
              background: langOpen ? TOKENS.surfaceSkeleton : "transparent",
              color: TOKENS.textSecondary,
              border: `1px solid ${TOKENS.borderDefault}`,
              borderRadius: TOKENS.radiusSm,
              cursor: "pointer",
            }}
          >
            <span>{activeLabel}</span>
            <span aria-hidden="true" style={{ fontSize: "var(--text-xs)" }}>
              v
            </span>
          </button>
          <LanguageMenu open={langOpen} onClose={() => setLangOpen(false)} />

          <VariantUserMenuTrigger
            variant={variant}
            identity={identity}
            onToggle={handleUserToggle}
            expanded={userOpen}
          />
          {variant !== "apex" && variant !== "operator" ? (
            <UserMenu open={userOpen} onClose={() => setUserOpen(false)} />
          ) : null}
        </div>
      </header>

      {renderSidebarAndFooter && sidebar !== undefined ? (
        <div
          style={{
            display: "flex",
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          {sidebar}
          <main
            id="app-root"
            data-testid={contentTestId ?? "app-root"}
            aria-labelledby={contentAriaLabelledBy}
            tabIndex={-1}
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "auto",
              padding: TOKENS.space[6],
            }}
          >
            {children}
          </main>
        </div>
      ) : (
        <main
          id="app-root"
          data-testid={contentTestId ?? "app-root"}
          aria-labelledby={contentAriaLabelledBy}
          tabIndex={-1}
          style={{
            flex: 1,
            minWidth: 0,
            overflow: "auto",
            padding: TOKENS.space[8],
          }}
        >
          {children}
        </main>
      )}

      {renderSidebarAndFooter ? <ChromeFooter /> : null}
    </div>
  );
}

function pickName(profile: unknown): string {
  if (!profile || typeof profile !== "object") return "";
  const p = profile as Record<string, unknown>;
  for (const key of ["name", "preferred_username", "email"]) {
    const value = p[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return "";
}
